// import { useThrottledCallback } from "@corpuscomplete/web/hooks/debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { useThrottledCallback } from "@tanstack/react-pacer";

type UseSyncedInputProps = {
  syncedValue: string;
  save: (value: string) => void;
  mode?: "input" | "textarea";
  onFocus?: () => void;
};

export function useSyncedInput({
  syncedValue,
  save,
  mode = "input",
  onFocus: _onFocus,
}: UseSyncedInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  const [value, _setValue] = useState(syncedValue);
  const valueRef = useRef(syncedValue);
  const setValue = useCallback((newValue: string) => {
    valueRef.current = newValue;
    _setValue(newValue);
  }, []);
  const rawSave = () => {
    if (syncedValue !== valueRef.current) {
      save(valueRef.current);
    }
  };
  const throttledSave = useThrottledCallback(rawSave, {
    wait: 500,
  });

  useEffect(() => {
    // TODO: Maybe sync if isFocused is true but the value is the same as the original when starting editing?
    if (!isFocused) {
      setValue(syncedValue);
    }
  }, [syncedValue, isFocused, setValue]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    throttledSave();
  };

  const onFocus = () => {
    setIsFocused(true);
    setValue(syncedValue);
    _onFocus?.();
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      e.currentTarget.blur();
    }
    if (e.key === "Enter") {
      if (mode === "textarea") {
        return;
      }
      e.currentTarget.blur();
    }
  };

  return {
    value,
    isFocused,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
  };
}
